import { useEffect, useMemo, useState } from 'react'

import { logger } from '~/debug/logger'
import { usePrincipalOrgId } from '~/store/slices/currentOrg'

import type { GridConnectionModel } from '../client/ders'
import { useGetGridConnectionsQuery } from '../client/ders'
import { combineSkip } from '../hooks/utils'
import type { IndexedDbService } from './storage/IndexedDbService'
import type { GridConnectionCacheRecord } from './storage/schema'
import { useGateValue } from '~/configs/statsig'

const GRID_CONNECTIONS_PAGE_SIZE = 1000

// Stable reference for the gated-off path (the common case for most orgs), so a
// fresh [] doesn't bust the manager's useMemo and re-render every accessor.
const EMPTY_GRID_CONNECTIONS: GridConnectionModel[] = []

export type GridConnectionsDataManagerContextValue = {
  data: GridConnectionModel[]
  isLoading: boolean
  isFetching: boolean
}

function useGridConnectionsQuery(enabled: boolean, options?: { skip?: boolean }) {
  const principalOrgId = usePrincipalOrgId()

  const { data, isLoading, isFetching } = useGetGridConnectionsQuery(
    {
      orgIds: principalOrgId ? [principalOrgId] : undefined,
      orgDepth: 100,
      includeLocalManaged: true,
      page: 0,
      count: GRID_CONNECTIONS_PAGE_SIZE
    },
    combineSkip(!principalOrgId || !enabled, options)
  )

  return {
    gridConnections: data?.data,
    isLoading,
    isFetching
  }
}

export default function useGridConnectionsDataManager(service: IndexedDbService) {
  // Grid connections are behind the grid-connections-in-ui gate. Like fuel
  // tanks, this both skips the fetch AND withholds any hydrated cache for orgs
  // without the feature, so a cache populated in a prior gated-on session can't
  // leak connections into surfaces that no longer check the gate while detail
  // pages still 404.
  const hasGridConnectionsInUi = useGateValue('grid-connections-in-ui')
  const { gridConnections, isLoading, isFetching } = useGridConnectionsQuery(hasGridConnectionsInUi)

  const data = useGridConnectionsPersistence(service, gridConnections, hasGridConnectionsInUi)

  return useMemo<GridConnectionsDataManagerContextValue>(
    () => ({
      data,
      isLoading,
      isFetching
    }),
    [data, isLoading, isFetching]
  )
}

function useGridConnectionsPersistence(
  service: IndexedDbService,
  gridConnections: GridConnectionModel[] | undefined,
  enabled: boolean
): GridConnectionModel[] {
  const [hydratedGridConnections, setHydratedGridConnections] = useState<GridConnectionModel[]>([])

  useEffect(() => {
    // Don't surface a previously-cached set when the feature is gated off.
    if (!enabled) {
      setHydratedGridConnections([])
      return
    }

    async function hydrateGridConnectionsCache() {
      try {
        const records = await service.getAll('gridConnections')
        setHydratedGridConnections(records.map((record) => record.payload))
      } catch (error) {
        logger.error('Failed to hydrate grid connections cache', error)
      }
    }

    void hydrateGridConnectionsCache()
  }, [service, enabled])

  useEffect(() => {
    if (!gridConnections) return

    const fetchedAt = Date.now()
    const records: GridConnectionCacheRecord[] = gridConnections.map((gridConnection) => ({
      id: gridConnection.id,
      payload: gridConnection,
      syncedAt: fetchedAt
    }))

    const replaceRecords = async () => {
      try {
        await service.replace('gridConnections', records)
      } catch (error) {
        logger.error('Failed to persist grid connections snapshot', error)
      }
    }

    void replaceRecords()
  }, [gridConnections, service])

  return enabled ? (gridConnections ?? hydratedGridConnections) : EMPTY_GRID_CONNECTIONS
}
